import React from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment-timezone';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BaseFormComponent } from '../../shared/baseFormComponent';
import { ErrorsBlockComponent } from '../../shared/errorsBlockComponent';
import { ButtonBusyIndicatorComponent } from '../../shared/buttonBusyIndicatorComponent';
import { Link } from '../../shared/link';
import { AssignProperties, ToString, MakeApiCall, FormatString, PrepareError, CreateTimer, PreventDOMLock, DisplayErrors, ReplaceUrl, SetFocusOnFirstFormControl } from '../../shared/utils';

import './styles.scss';

export const AUTH_MODE = {
    LOGIN: 'LOGIN',
    REGISTER: 'REGISTER',
}

const AUTH_ACTION = {
    NONE: 0,
    LOGIN: 1,
    REGISTER: 2,
    WAIT_OTP: 3,
    CHECK_OTP: 4
}

class LoginForm extends BaseFormComponent {
    constructor(props, ...others) {
        super(props, ...others);
        this.strings = {...this.constructor.defaultProps.strings};
        AssignProperties(this.strings, props.strings);

        this.state = {
            passwordVisible: false,
        }

        this._valueHandler.onBlurOptions = {
            'Email': BaseFormComponent.EmailOptions,
        }

        this.handleSwitchToRegister = this.handleSwitchToRegister.bind(this);
        this.togglePasswordVisible = this.togglePasswordVisible.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleSwitchToRegister() {
        const {onModeChange} = this.props;

        if (typeof onModeChange === 'function') {
            PreventDOMLock(() => onModeChange(AUTH_MODE.REGISTER));
        }
    }

    togglePasswordVisible() {
        const self = this;

        if (self._isMounted) {
            self.setState({passwordVisible: !self.state.passwordVisible});
        }
    }

    render() {
        const self = this;
        const {action, data, disabled, errors, submitCaption, registerUrl} = self.props;
        const {passwordVisible} = self.state;
        const formData = {...self.constructor.defaultProps.data, ...data};
        const strings = self.strings;
        let recoverPasswordUrl = '/RecoverPassword';

        if (Boolean(formData.Email)) {
            recoverPasswordUrl += '?email=' + encodeURIComponent(formData.Email);
        }

        return (
            <div id="login-block">
                {/* <div className="section-description section-description--second color-white uppercase">{strings.TITLE_WelcomeBack}</div> */}
                <h1 className="section-title color-white m-0">{strings.TITLE_LogIn}</h1>
                {self.props.socialButtons}
                <div className="white-panel white-panel--login">
                    <div className="white-panel__body">
                        <div className="form-group">
                            <input 
                                type="text" 
                                id="login-email"
                                name="Email" 
                                placeholder={strings.PH_Email}
                                autoComplete="email"
                                onChange={self.handleInputChange}
                                onBlur={self.handleInputChange}
                                className={self.getControlClass('Email', errors) || null} 
                                value={formData.Email} 
                                disabled={disabled} 
                            />
                            <label className="is-icon" htmlFor="login-email">
                                <svg className="icon icon-email">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#email" />
                                </svg>
                            </label>
                            <div className="invalid-feedback">{self.getControlError('Email', errors)}</div>
                        </div>
                        <div className="form-group">
                            <input 
                                type={passwordVisible ? 'text' : 'password'}
                                id="login-password"
                                name="Password" 
                                placeholder={strings.PH_Password}
                                autoComplete="current-password"
                                onChange={self.handleInputChange}
                                onBlur={self.handleInputChange}
                                className={self.getControlClass('Password', errors) || null} 
                                value={formData.Password} 
                                disabled={disabled} 
                            />
                            <label className="is-icon" htmlFor="LoginPassword">
                                <svg className="icon icon-password">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#password" />
                                </svg>
                            </label>
                            <button 
                                type="button" 
                                className={'toggle-password' + (passwordVisible ? ' is-active' : '')} 
                                onClick={self.togglePasswordVisible}
                            >
                                <svg className="icon icon-eye">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#eye" />
                                </svg>
                            </button>
                            <div className="invalid-feedback">{self.getControlError('Password', errors)}</div>
                        </div>                    
                        <div className="form-group">
                            <div className="flex flex-horizontal-beetween flex-vertical-center">
                                <div className="flex">
                                    <a className="forgot-link" href={recoverPasswordUrl}>{strings.A_ForgotPassword}</a>
                                </div>
                            </div>
                        </div>
                        <div className="form-group form-group--last">
                            <button 
                                className={'btn btn--yellow btn--full' + (disabled ? ' btn--disabled' : '')} 
                                type="submit" 
                                disabled={disabled}
                            >
                                {submitCaption || strings.BTN_LogIn}
                                <ButtonBusyIndicatorComponent visible={action == AUTH_ACTION.LOGIN} />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="login__toggle">
                    {strings.M_NoAccount}&nbsp;<Link to={registerUrl || self.handleSwitchToRegister} disabled={disabled}>{strings.A_RegisterForFree}</Link>
                </div>
            </div>
        );        
    }
}
LoginForm.propTypes = {
    strings: PropTypes.object.isRequired,
    action: PropTypes.number,
    data: PropTypes.object,
    disabled: PropTypes.bool,
    errors: PropTypes.object,
    registerUrl: PropTypes.string,
    submitCaption: PropTypes.string,
}
LoginForm.defaultProps = {
    strings: {
        TITLE_WelcomeBack: 'Welcome back',
        TITLE_LogIn: 'Log In',
        M_NoAccount: 'Don\'t have an account?',
        A_RegisterForFree: 'Sign up for free',
        A_ForgotPassword: 'Forgot password?',
        BTN_LogIn: 'Log In',
        PH_Email: 'Email',
        PH_Password: 'Password',
    },
    disabled: false,
    errors: null,
    data: {
        Email: '',
        Password: ''
    }
}

class RegisterForm extends BaseFormComponent{
    constructor(props, ...others) {
        super(props, ...others);
        this.strings = {...this.constructor.defaultProps.strings};
        AssignProperties(this.strings, props.strings);

        this.state = {
            passwordVisible: false,
            timeZones: this.mapTimeZones(props.timeZones)
        }

        this.handleSwitchToLogIn = this.handleSwitchToLogIn.bind(this);
        this.togglePasswordVisible = this.togglePasswordVisible.bind(this);

        this._valueHandler.onBlurOptions = {
            'Name': BaseFormComponent.OPTIONS.AllTrim,
            'Email': BaseFormComponent.EmailOptions,
        }
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    mapTimeZones(timeZonesList) {
        let result = [];

        if (Boolean(timeZonesList)) {
            const utcNow = moment.utc();
            const currentTimeZoneId = moment.tz.guess(true);

            result = timeZonesList
                .map(p => {
                    return {
                        value: p.Id,
                        label: '(UTC' + utcNow.clone().tz(p.Id).format('Z') + ') ' + p.Name,
                        ids: p.Ids.slice(),
                        isCurrent: (p.Id == currentTimeZoneId) || p.Ids.some(id => id == currentTimeZoneId)
                    }
                })
                .sort((p1, p2) => ToString(p1.label).localeCompare(p2.label));
        }

        return result;
    }

    componentDidUpdate(prevProps) {
        const self = this;
        const props = self.props;
        let newState = null;

        if (props.timeZones != prevProps.timeZones) {
            newState = {...newState, timeZones: self.mapTimeZones(props.timeZones)};
        }

        if (Boolean(newState)) {
            self.setState(newState);
        }
    }

    handleSwitchToLogIn() {
        const {onModeChange} = this.props;

        if (typeof onModeChange === 'function') {
            PreventDOMLock(() => onModeChange(AUTH_MODE.LOGIN));
        }
    }

    togglePasswordVisible() {
        const self = this;

        if (self._isMounted) {
            self.setState({passwordVisible: !self.state.passwordVisible});
        }
    }

    render() {
        const self = this;
        const {action, data, disabled, errors, submitCaption} = self.props;
        const {timeZones, passwordVisible} = self.state;
        const strings = self.strings;

        const formData = {...self.constructor.defaultProps.data, ...data};
        const isSubmitAllowed = !disabled && formData.IsAgree;
        const selectedTimeZone =
            timeZones.find(p => p.value == formData.TimeZoneId) ??
            timeZones.find(p => p.ids.some(id => id == formData.TimeZoneId));

        return (
            <div id="register-block">
                {/* <div className="section-description section-description--second color-white uppercase">{strings.TITLE_StartForFree}</div> */}
                <h1 className="section-title color-white m-0">{strings.TITLE_Register}</h1>
                {self.props.socialButtons}
                <div className="white-panel white-panel--login">
                    <div className="white-panel__body">
                        <div className="form-group">
                            <input 
                                type="text" 
                                id="register-fullname"
                                name="Name" 
                                placeholder={strings.PH_FullName} 
                                autoComplete="name"
                                onChange={self.handleInputChange}
                                onBlur={self.handleInputChange}
                                className={self.getControlClass('Name', errors) || null}
                                value={formData.Name} 
                                disabled={disabled} 
                            />
                            <label className="is-icon" htmlFor="Name">
                                <svg className="icon icon-user">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#user" />
                                </svg>
                            </label>
                            <div className="invalid-feedback">{self.getControlError('Name', errors)}</div>
                        </div>
                        <div className="form-group">
                            <input 
                                type="text" 
                                id="register-email"
                                name="Email" 
                                placeholder={strings.PH_Email} 
                                autoComplete="email"
                                onChange={self.handleInputChange}
                                onBlur={self.handleInputChange}
                                className={self.getControlClass('Email', errors) || null}
                                value={formData.Email} 
                                disabled={disabled} 
                            />
                            <label className="is-icon" htmlFor="Email">
                                <svg className="icon icon-email">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#email" />
                                </svg>
                            </label>
                            <div className="invalid-feedback">{self.getControlError('Email', errors)}</div>
                        </div>
                        <div className="form-group">
                            <input 
                                type={passwordVisible ? 'text' : 'password'} 
                                id="register-password"
                                name="Password"
                                placeholder={strings.PH_NewPassword}
                                autoComplete="new-password"
                                onChange={self.handleInputChange}
                                onBlur={self.handleInputChange}
                                className={self.getControlClass('Password', errors) || null} 
                                value={formData.Password} 
                                disabled={disabled} 
                            />
                            <label className="is-icon" htmlFor="Password">
                                <svg className="icon icon-password">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#password" />
                                </svg>
                            </label>
                            <button 
                                type="button"
                                className={'toggle-password' + (passwordVisible ? ' is-active' : '')}
                                onClick={self.togglePasswordVisible}
                            >
                                <svg className="icon icon-eye">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#eye" />
                                </svg>
                            </button>
                            <div className="invalid-feedback">{self.getControlError('Password', errors)}</div>
                        </div>
                        <div className="form-group form-group--select">
                            <select
                                id="TimeZoneId"
                                name="TimeZoneId"
                                className={self.getControlClass('TimeZoneId', errors) || null}
                                onChange={self.handleInputChange}
                                value={selectedTimeZone?.value || ''}
                                disabled={disabled}
                                required
                            >
                                {(selectedTimeZone == null) && <option key={-1} value="" disabled hidden>{strings.PH_TimeZone}</option>}
                                {timeZones.map(p => {
                                    let result;

                                    if (p.isCurrent) {
                                        result = <option key={p.value} value={p.value} className="fw-bold">{p.label}</option>
                                    }
                                    else {
                                        result = <option key={p.value} value={p.value}>{p.label}</option>;
                                    }

                                    return result;
                                })}
                            </select>
                            <label className="is-icon" htmlFor="TimeZoneId">
                                <svg className="icon icon-time">
                                    <use xlinkHref="/assets/images/icons/sprite-inline.svg#time"/>
                                </svg>
                            </label>
                            <div className="invalid-feedback">{self.getControlError('TimeZoneId', errors)}</div>
                        </div>
                        <div className="form-group">
                            <input 
                                type="checkbox" 
                                id="IsAgree"
                                name="IsAgree"
                                value={formData.IsAgree} 
                                onChange={self.handleInputChange}
                                disabled={disabled}
                                required
                            />
                            <label htmlFor="IsAgree">
                                {strings.M_AgreeTo}&nbsp;
                                <Link to="/terms" target="_blank">{strings.A_Terms}</Link>&nbsp;{strings.M_And}&nbsp;<Link to="/privacy" target="_blank">{strings.A_PrivacyPolicy}</Link>
                            </label>
                        </div>
                        <div className="form-group form-group--last">
                            <button 
                                type="submit" 
                                className={'btn btn--yellow btn--full' + (!isSubmitAllowed ? ' btn--disabled' : '')} 
                                disabled={!isSubmitAllowed}
                            >
                                {submitCaption || strings.BTN_Register}
                                <ButtonBusyIndicatorComponent visible={action == AUTH_ACTION.REGISTER} />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="login__toggle">
                    {strings.M_HaveAccount}&nbsp;<Link to={self.handleSwitchToLogIn} disabled={disabled}>{strings.A_LogIn}</Link>
                </div>
            </div>
        );
    }
}
RegisterForm.propTypes = {
    strings: PropTypes.object.isRequired,
    data: PropTypes.object,
    disabled: PropTypes.bool,
    errors: PropTypes.object,
    timeZones: PropTypes.array.isRequired,
    submitCaption: PropTypes.string,
}
RegisterForm.defaultProps = {
    strings: {
        TITLE_StartForFree: 'Start for free',
        TITLE_Register: 'Create new account',
        A_LogIn: 'Log In',
        A_Terms: 'Terms',
        A_PrivacyPolicy: 'Privacy Policy',
        M_AgreeTo: 'I agree to',
        M_HaveAccount: 'Have an account?',
        M_And: 'and',
        PH_FullName: 'Your name',
        PH_Email: 'Email',
        PH_NewPassword: 'Password (6 or more characters)',
        PH_TimeZone: 'Your timezone',

        BTN_Register: 'Sign Up',
    },
    disabled: false,
    errors: null,
    data: {
        Email: '',
        Name: '',
        Password: '',
        TimeZoneId: ''
    }
}

export class AuthFormComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.strings = {...this.constructor.defaultProps.strings};
        AssignProperties(this.strings, props.strings);

        this.state = {
            mode: Object.keys(AUTH_MODE).some(k => AUTH_MODE[k] == props.mode)
                ? props.mode 
                : AUTH_MODE.LOGIN,
            action: AUTH_ACTION.NONE, 
            loginErrors: null, 
            registerErrors: null,
            socialErrors: null,
            twoFAErrors: null,
            reCaptchaAPILoaded: false,
            loginData: {
                Email: '', 
                Password: ''
            },
            registerData: {
                Name: '', 
                Email: '', 
                Password: '', 
                TimeZoneId: props.timeZoneId || moment.tz.guess(true), 
                CountryId: props.countryId || ''
            },
            OTPToken: '',
            OTPMessage: '',
            OTPErrorMessage: '',
            OTP: '',
            location: {
                hash: location.hash, 
                search: location.search
            },
        };

        this.handleModeChange = this.handleModeChange.bind(this);
        this.handleLoginDataChange = this.handleLoginDataChange.bind(this);
        this.handleRegisterDataChange = this.handleRegisterDataChange.bind(this);
        this.handleOTPChanged = this.handleOTPChanged.bind(this);
        this.handleLoginSubmit = this.handleLoginSubmit.bind(this);
        this.handleRegisterSubmit = this.handleRegisterSubmit.bind(this);
        this.handleSocialAuthClick = this.handleSocialAuthClick.bind(this);
        this.doOk = this.doOk.bind(this);
        this.doLogin = this.doLogin.bind(this);
        this.doRegister = this.doRegister.bind(this);
        this.doSocialAuth = this.doSocialAuth.bind(this);
        this.renderSocialButton = this.renderSocialButton.bind(this);

        this.handleOTPDialogCloseClick = this.handleOTPDialogCloseClick.bind(this);

        this.getReCaptchaToken = this.getReCaptchaToken.bind(this);
        this.reCaptchaTimer = CreateTimer(200);
        this.checkReCaptchaLoaded = this.checkReCaptchaLoaded.bind(this);
    }

    checkReCaptchaLoaded() {
        const self = this;

        self.reCaptchaTimer.stop();

        const grecaptchaApi = window['grecaptcha'];
        if (Boolean(grecaptchaApi) && !self.state.reCaptchaAPILoaded) {
            grecaptchaApi.ready(
                () => {
                    if (self._isMounted) {
                        self.setState({reCaptchaAPILoaded: true});
                    }
                }
            )
        }

        if (!Boolean(grecaptchaApi) && self._isMounted) {
            self.reCaptchaTimer.start();
        }
    }

    componentDidMount() {
        const self = this;
        self._isMounted = true;

        self.reCaptchaTimer.onTimer = self.checkReCaptchaLoaded;
        self.checkReCaptchaLoaded();

        DisplayErrors(self.props.socialErrors) ;
    }

    componentWillUnmount() {
        const self = this;
        self._isMounted = false;
        self.reCaptchaTimer.stop();
    }

    componentDidUpdate(_, prevState) {
        const self = this;

        if ((prevState.action != AUTH_ACTION.WAIT_OTP) && (self.state.action == AUTH_ACTION.WAIT_OTP)) {
            setTimeout(() => SetFocusOnFirstFormControl('#Auth\\.dlgOTP .modal__content'), 0);
        }
    }

    getReCaptchaToken() {
        const self = this;
        const {reCaptchaSiteKey} = self.props;
        if (!Boolean(reCaptchaSiteKey)) {
            return Promise.resolve('');
        }

        const grecaptchaApi = window['grecaptcha'];
        if (!self.state.reCaptchaAPILoaded) {
            return Promise.reject('ReCAPTCHA API was not loaded.');
        }
        else {
            return new Promise((resolve, reject) => {
                try {
                    grecaptchaApi.execute(reCaptchaSiteKey, {action: 'homepage'})
                        .then(token => {
                            resolve(token);
                        });
                }
                catch (error) {
                    //reject(error);
                    reject('ReCAPTCHA API error.');
                }
            })
        }
    }

    handleModeChange(mode) {
        const self = this;

        if (Object.keys(AUTH_MODE).some(k => AUTH_MODE[k] == mode) && self._isMounted) {
            let pathname;

            switch (mode) {
                case AUTH_MODE.LOGIN:
                    pathname = '/login';
                    break;
                case AUTH_MODE.REGISTER:
                    pathname = '/register';
                    break;
            }

            if (pathname) {
                ReplaceUrl({...self.state.location, pathname});
            }

            self.setState({mode: mode});
        }
    }

    handleLoginDataChange(name, value) {
        const self = this;
        const {loginErrors, loginData} = self.state;

        if (self._isMounted && (loginData[name] != value)) {
            const errors = {...loginErrors};
            delete errors[name];

            self.setState({loginData: {...loginData, [name]: value}, loginErrors: errors});
        }
    }

    handleRegisterDataChange(name, value) {
        const self = this;
        const {registerErrors, registerData} = self.state;

        if (self._isMounted && (registerData[name] != value)) {
            const errors = {...registerErrors};
            delete errors[name];

            self.setState({registerData: {...registerData, [name]: value}, registerErrors: errors});
        }
    }

    handleOTPChanged(e) {
        const self = this;
        const value = ToString(e.target.value);

        PreventDOMLock(() => {
            if (self._isMounted && (self.state.OTP != value)) {
                self.setState({OTP: value});
            }
        });
    }

    handleLoginSubmit(event) {
        event.preventDefault();
        const self = this;

        PreventDOMLock(self.doLogin);
    }

    handleRegisterSubmit(event) {
        event.preventDefault();
        const self = this;

        PreventDOMLock(self.doRegister);
    }

    handleSocialAuthClick(event) {
        const self = this;
        const providerId = event.currentTarget.getAttribute('data-id');

        if (Boolean(providerId)) {
            PreventDOMLock(() => {
                self.doSocialAuth(providerId);
            });
        }
    }

    doOk(url) {
        const self = this;
        if (self._isMounted) {
            url = (ToString(url) || ToString(self.props.returnUrl)).trim();
            window.location.assign(url);

            //If URL is link to resource (file for example), then redirecting to "Home" page after short pause
            //setTimeout(() => window.location.assign('/'), 4000);
        }
    }

    doLogin() {
        const self = this;
        const {action, OTP, OTPToken, loginData} = self.state;
        const {loginApiUrl, verificationToken, returnUrl} = self.props;

        if (!Boolean(loginApiUrl) || !self._isMounted) {
            return;
        }

        let resetPasswordOnError = false;
        const data = {...loginData, ReturnUrl: returnUrl};

        if (action == AUTH_ACTION.WAIT_OTP) {
            data.OTP = ToString(OTP).trim();
            data.OTPToken = OTPToken;

            resetPasswordOnError = true;
        }

        const newAction = (action == AUTH_ACTION.WAIT_OTP)
            ? AUTH_ACTION.CHECK_OTP
            : AUTH_ACTION.LOGIN;

        new Promise(resolve => {
            self.setState({action: newAction, socialErrors: null, loginErrors: null, registerErrors: null, OTPErrorMessage: ''}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return self.getReCaptchaToken();
            }
        })
        .then(token => {
            if (self._isMounted) {
                data.ReCaptchaToken = token;

                return MakeApiCall(
                    $.post(
                        {
                            url: loginApiUrl,
                            headers: {RequestVerificationToken: verificationToken},
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ));
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                if (responseData.OTPToken) {
                    const newState = {
                        action: AUTH_ACTION.WAIT_OTP, 
                        OTP: '', 
                        OTPToken: responseData.OTPToken, 
                        OTPErrorMessage: responseData.OTPErrorMessage
                    }

                    if (Boolean(responseData.OTPMessage)) {
                        newState.OTPMessage = responseData.OTPMessage;
                    }

                    self.setState(newState);
                }
                else {
                    self.doOk(responseData.SuccessUrl);
                }
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                const loginErrors = PrepareError(errors);
                const newState = {action: AUTH_ACTION.NONE, loginErrors: loginErrors};

                if (resetPasswordOnError) {
                    newState.loginData = {...self.state.loginData, Password: ''};
                }

                self.setState(newState);

                DisplayErrors(ErrorsBlockComponent.filterErrors(loginErrors, 'Email Password'));
            }
        });
    }

    doRegister() {
        const self = this;
        const {registerApiUrl, verificationToken, returnUrl} = self.props;

        if (!Boolean(registerApiUrl) || !self._isMounted) {
            return;
        }

        const data = {...self.state.registerData, ReturnUrl: returnUrl};

        new Promise(resolve => {
            self.setState({action: AUTH_ACTION.REGISTER, socialErrors: null, loginErrors: null, registerErrors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return self.getReCaptchaToken();
            }
        })
        .then(token => {
            if (self._isMounted) {
                data.ReCaptchaToken = token;

                return MakeApiCall(
                    $.post(
                        {
                            url: registerApiUrl,
                            headers: {RequestVerificationToken: verificationToken},
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ));
            }
        })
        .then(responseData => {
            self.doOk(responseData.SuccessUrl);
        })
        .catch(errors => {
            if (self._isMounted) {
                const registerErrors  = PrepareError(errors);

                self.setState({action: AUTH_ACTION.NONE, registerErrors: registerErrors});
                DisplayErrors(ErrorsBlockComponent.filterErrors(registerErrors, 'Name Email Password CountryId TimeZoneId'));
            }
        });
    }

    doSocialAuth(providerId) {
        const self = this;
        const {socialAuthApiUrl, verificationToken, returnUrl} = self.props;

        if (!Boolean(socialAuthApiUrl) || !self._isMounted) {
            return;
        }

        const data = {Provider: providerId, ReturnUrl: returnUrl};

        new Promise(resolve => {
            if (self._isMounted) {
                self.setState({action: providerId, socialErrors: null, loginErrors: null, registerErrors: null}, resolve);
            }
            else {
                reject(null);
            }
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.post(
                        {
                            url: socialAuthApiUrl,
                            headers: {RequestVerificationToken: verificationToken},
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted);
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                self.doOk(responseData.SuccessUrl);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                const socialErrors = PrepareError(errors);

                self.setState({action: AUTH_ACTION.NONE, socialErrors: socialErrors});
                DisplayErrors(socialErrors);
            }
        });
    }

    renderSocialButton(providerInfo, isDisabled) {
        const self = this;
        const {action, mode} = self.state;
        const titleTemplate = (mode == AUTH_MODE.LOGIN) ? self.strings.BTN_LogInWith : self.strings.BTN_SignUpWith;

        return (
            <button
                key={providerInfo.ProviderName}
                type="button"
                className={'social-btn' + (isDisabled ? ' btn--disabled' : '')}
                disabled={isDisabled}
                data-id={providerInfo.ProviderName}
                onClick={self.handleSocialAuthClick}
            >
                <svg className={`icon icon-login-${providerInfo.DisplayName.toLowerCase()}`}>
                    <use xlinkHref={`/assets/images/icons/sprite-inline.svg#login-${providerInfo.DisplayName.toLowerCase()}`} />
                </svg>
                {FormatString(titleTemplate, providerInfo.DisplayName)}
                <ButtonBusyIndicatorComponent visible={action == providerInfo.ProviderName} />
            </button>
        )
    }

    handleOTPDialogCloseClick(e) {
        const self = this;

        if ((e.type !== 'keydown') || (e.keyCode == 27)) {
            if (self._isMounted) {
                self.setState({action: AUTH_ACTION.NONE});
            }
        }
    }

    renderOTPDialog() {
        const self = this;
        const {action, OTP, OTPMessage, OTPErrorMessage} = self.state;

        if ([AUTH_ACTION.WAIT_OTP, AUTH_ACTION.CHECK_OTP].indexOf(action) < 0) {
            return null;
        }

        const strings = self.strings;
        const isSubmitDisabled = (action != AUTH_ACTION.WAIT_OTP) || !Boolean(OTP);

        return (
            <div 
                className="modal is-open" 
                id="Auth.dlgOTP" 
                tabIndex={100} 
                onKeyDown={self.handleOTPDialogCloseClick}
            >
                <div className="modal__bg" />
                <div className="modal__inner">
                    <div className="modal__box">
                        <div 
                            className="modal__close" 
                            onClick={self.handleOTPDialogCloseClick}
                        >
                            <svg className="icon icon-close">
                                <use xlinkHref="/assets/images/icons/sprite-inline.svg#close" />
                            </svg>
                        </div>
                        <div className="modal__app">
                            <div className="modal__content">
                                <h3>Authentication required</h3>
                                <div className="modal__content-desc">{OTPMessage}</div>
                                <form onSubmit={self.handleLoginSubmit}>
                                    <div className="form-group form-group--default mt-30">
                                        <input 
                                            type="text" 
                                            id="otpCode" 
                                            name="otpCode"
                                            placeholder={strings.PH_OTP}
                                            className={Boolean(OTPErrorMessage) ? ' is-invalid' : null}
                                            autoComplete="off"
                                            value={OTP}
                                            onChange={self.handleOTPChanged}
                                            readOnly={action != AUTH_ACTION.WAIT_OTP}
                                        />
                                        <div className="invalid-feedback">{OTPErrorMessage}</div>
                                    </div>
                                    <div className="form-group form-group--last">
                                        <button 
                                            type="submit"
                                            className={'btn btn--yellow btn--full' + (isSubmitDisabled ? ' btn--disabled' : '')}
                                            disabled={isSubmitDisabled}
                                        >
                                            {strings.BTN_SubmitOTP}
                                            <ButtonBusyIndicatorComponent visible={action == AUTH_ACTION.CHECK_OTP} />
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const self = this;
        const {mode, action, loginErrors, registerErrors, loginData, registerData, reCaptchaAPILoaded} = self.state;
        const {authMessage, loginButtonCaption, registerUrl, registerButtonCaption, socialAuthApiUrl, timeZones, socialProviders, reCaptchaSiteKey} = self.props;
        const isDisabled = action !== AUTH_ACTION.NONE;
        const strings = self.strings;
        const isAuthAllowed = !Boolean(reCaptchaSiteKey) || reCaptchaAPILoaded;
        const socialButtons = Boolean(socialProviders) && (socialProviders.length > 0) && Boolean(socialAuthApiUrl) &&
            <div className="social-auth">
                <div className="social-auth__content">
                    {socialProviders.map(p => self.renderSocialButton(p, isDisabled))}
                </div>
                <div className="social-auth__bottom">or</div>
            </div>

        return (
            <HelmetProvider>
                <Helmet>
                    {(mode == AUTH_MODE.LOGIN) && <meta name="description" content="Log in to automate all database backups from a single place on the web. Connect to your databases (SQL Server, MySQL, PostgreSQL) and send backups to any cloud storage." />}
                    {(mode == AUTH_MODE.REGISTER) && <meta name="description" content="Sign up to automate all database backups from a single place on the web. Connect to your databases (SQL Server, MySQL, PostgreSQL) and send backups to any cloud storage." />}
                </Helmet>
                {self.renderOTPDialog()}
                {Boolean(authMessage) &&
                    <div className="alert alert--info">{authMessage}</div>
                }
                {(mode == AUTH_MODE.LOGIN) && 
                    <form 
                        id="login-form" 
                        name="LoginForm" 
                        onSubmit={isAuthAllowed ? self.handleLoginSubmit : null}
                    >
                        <LoginForm 
                            strings={strings}
                            action={action}
                            errors={loginErrors}
                            data={loginData}
                            disabled={isDisabled}
                            onChange={self.handleLoginDataChange}
                            onModeChange={self.handleModeChange}
                            submitCaption={loginButtonCaption}
                            registerUrl={registerUrl}
                            socialButtons={socialButtons}
                        />
                    </form>
                }
                {(mode == AUTH_MODE.REGISTER) && 
                    <form 
                        id="register-form" 
                        name="RegisterForm" 
                        onSubmit={isAuthAllowed ? self.handleRegisterSubmit : null}
                    >
                        <RegisterForm 
                            strings={strings}
                            action={action}
                            errors={registerErrors}
                            data={registerData}
                            disabled={isDisabled}
                            onChange={self.handleRegisterDataChange}
                            onModeChange={self.handleModeChange}
                            submitCaption={registerButtonCaption}
                            timeZones={timeZones}
                            socialButtons={socialButtons}
                        />
                    </form>
                }
            </HelmetProvider>
        );
    }
}
AuthFormComponent.propTypes = {
    strings: PropTypes.object,
    verificationToken: PropTypes.string.isRequired,
    returnUrl: PropTypes.string,
    loginApiUrl: PropTypes.string.isRequired,
    registerApiUrl: PropTypes.string.isRequired,
    registerUrl: PropTypes.string,
    socialAuthApiUrl: PropTypes.string,
    countryId: PropTypes.string,
    timeZoneId: PropTypes.string,
    timeZones: PropTypes.arrayOf(PropTypes.object).isRequired,
    socialProviders: PropTypes.arrayOf(PropTypes.object),
    reCaptchaSiteKey: PropTypes.string,

    authMessage: PropTypes.string,
    socialErrors: PropTypes.object,
    loginButtonCaption: PropTypes.string,
    registerButtonCaption: PropTypes.string,

    mode: PropTypes.string,
}
AuthFormComponent.defaultProps = {
    strings: {
        M_ContinueWithSocial: 'er continue with a social network',
        PH_OTP: 'Enter OTP',
        BTN_LogInWith: 'Login with {0}',
        BTN_SignUpWith: 'Sign up with {0}',
        BTN_SubmitOTP: 'Continue',
    },
    authMessage: '',
    returnUrl: '',
    socialAuthApiUrl: '',
    countryId: '',
    timeZoneId: '',
    socialProviders: null,
    loginButtonCaption: 'Log In',
    registerButtonCaption: 'Register',

    mode: AUTH_MODE.LOGIN,
}
